// Sentry client-side init (Next.js 15+ instrumentation API).
// Mirrors sms_form_app/src/main.tsx PHI scrubber — Sentry is NOT in our BAA
// scope, so any free-text PHI that slips into an error string MUST be
// redacted at egress.
//
// No-op until NEXT_PUBLIC_SENTRY_DSN is set in the Amplify env vars.

import * as Sentry from '@sentry/nextjs';

const _PHI_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/(?:authorization\s*[:=]\s*bearer\s+)\S+/gi, 'authorization: Bearer [REDACTED_BEARER]'],
  [/\beyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\b/g, '[REDACTED_JWT]'],
  [/\bsk-[A-Za-z0-9_\-]{20,}\b/g, '[REDACTED_KEY]'],
  [/\b(?:sk_|el_)[a-f0-9]{20,}\b/g, '[REDACTED_KEY]'],
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[REDACTED_EMAIL]'],
  [/\+1\d{10}\b/g, '[REDACTED_PHONE]'],
  [/(?:^|\s|\W)\+?\d{1,2}[ \-.]?\(?\d{3}\)?[ \-.]?\d{3}[ \-.]?\d{4}(?!\w)/g, ' [REDACTED_PHONE]'],
  [/(?<!\d)\d{3}[\- ]\d{2}[\- ]\d{4}(?!\d)/g, '[REDACTED_SSN]'],
  [/\b\d{2}[\/-]\d{2}[\/-]\d{4}\b/g, '[REDACTED_DATE]'],
];

function scrubPhi(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  let out = value;
  for (const [re, replacement] of _PHI_PATTERNS) out = out.replace(re, replacement);
  return out;
}

function scrubRecursive(value: unknown): unknown {
  if (typeof value === 'string') return scrubPhi(value);
  if (Array.isArray(value)) return value.map(scrubRecursive);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = scrubRecursive(v);
    return out;
  }
  return value;
}

function scrubEventPhi(event: Sentry.ErrorEvent): void {
  try {
    if (typeof event.message === 'string') event.message = scrubPhi(event.message) as string;

    const exValues = event.exception?.values;
    if (Array.isArray(exValues)) {
      for (const ex of exValues) {
        if (ex && typeof ex.value === 'string') ex.value = scrubPhi(ex.value) as string;
      }
    }

    const breadcrumbs = event.breadcrumbs;
    if (Array.isArray(breadcrumbs)) {
      for (const c of breadcrumbs) {
        if (c && typeof c.message === 'string') c.message = scrubPhi(c.message) as string;
        if (c && c.data && typeof c.data === 'object') c.data = scrubRecursive(c.data) as Record<string, unknown>;
      }
    }

    if (event.extra && typeof event.extra === 'object') {
      event.extra = scrubRecursive(event.extra) as Record<string, unknown>;
    }
    if (event.contexts && typeof event.contexts === 'object') {
      event.contexts = scrubRecursive(event.contexts) as typeof event.contexts;
    }
    if (event.tags && typeof event.tags === 'object') {
      const scrubbedTags: Record<string, string> = {};
      for (const [k, v] of Object.entries(event.tags)) {
        scrubbedTags[k] = typeof v === 'string' ? (scrubPhi(v) as string) : String(v);
      }
      event.tags = scrubbedTags;
    }

    // Strip request body — never safe to forward in this product.
    const req = (event as unknown as { request?: { data?: unknown; cookies?: unknown; headers?: Record<string, unknown> } }).request;
    if (req && typeof req === 'object') {
      delete req.data;
      delete req.cookies;
      if (req.headers && typeof req.headers === 'object') {
        const sensitive = new Set(['authorization', 'cookie', 'x-api-key']);
        for (const k of Object.keys(req.headers)) {
          if (sensitive.has(k.toLowerCase())) req.headers[k] = '[REDACTED]';
          else req.headers[k] = scrubPhi(req.headers[k]);
        }
      }
    }

    // Drop default user PII — keep only opaque id if present.
    const user = (event as unknown as { user?: { id?: string } }).user;
    if (user && typeof user === 'object') {
      const safeId = user.id;
      (event as unknown as { user?: { id: string } | undefined }).user = safeId ? { id: safeId } : undefined;
    }
  } catch {
    // Never let scrubbing failure block the event entirely — best-effort.
  }
}

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined,
    environment:
      process.env.NEXT_PUBLIC_ENVIRONMENT ||
      (typeof window !== 'undefined' && window.location.hostname.includes('staging')
        ? 'staging'
        : 'production'),
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*carecaller\.ai/,
      /^https:\/\/.*\.amplifyapp\.com/,
    ],
    beforeSend(event) {
      const msg = event.message || event.exception?.values?.[0]?.value || '';
      const type = event.exception?.values?.[0]?.type || '';

      // Drop transient network noise — patient device connectivity, Lambda cold starts.
      const networkErrors = [
        'Load failed', 'Failed to fetch', 'NetworkError', 'network error',
        'AbortError', 'ERR_NETWORK', 'ERR_INTERNET_DISCONNECTED',
        'ERR_CONNECTION', 'The network connection was lost', 'timeout',
        'fetch failed', 'net::',
      ];
      if (networkErrors.some(s => msg.includes(s) || type.includes(s))) return null;

      // Drop browser-extension noise.
      const extensionNoise = [
        'chrome-extension://', 'moz-extension://', 'safari-extension://',
        'runtime.sendMessage', 'Tab not found', 'Extension context',
        'extension:', 'No tab with id',
        'ResizeObserver loop',
        'Non-Error promise rejection captured',
        '__firefox__',
        'window.ethereum',
        'ethereum.selectedAddress',
      ];
      if (extensionNoise.some(s => msg.includes(s))) return null;

      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      if (frames.length > 0 && frames.every(f => (f.filename || '').includes('-extension://'))) return null;

      scrubEventPhi(event);
      return event;
    },
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
